import { useRequiredContext } from "@redotech/react-util/context";
import { ConversionFeature } from "@redotech/redo-conversion-settings-common/checkout-optimization-settings";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { memo } from "react";
import { Navigate, Outlet } from "react-router-dom";
import {
  ConversionSuiteStatusesContext,
  isConversionFeatureLocked,
} from "../../redo-suite/conversion-cloud";
import { IntentTrialExpired } from "../dashboard/intent-trial-expired";

/**
 * Route-level guard for Conversion Cloud surfaces.
 *
 * - Unpaid expired self-serve intent trial → full-suite lock page (same for
 *   every gated route, not Spotlights-only).
 * - Locked feature → dashboard redirect (dashboard also shows trial page when
 *   expired).
 * - Conversion (checkout optimization) off → welcome landing.
 * - Otherwise render the child route.
 */
export const ConversionFeatureGate = memo(function ConversionFeatureGate({
  feature,
}: {
  feature?: ConversionFeature;
}) {
  const {
    lockedFeatures,
    checkoutOptimizationEnabled,
    intentTrialExpiredUnpaid,
  } = useRequiredContext(ConversionSuiteStatusesContext);
  const routes = useRequiredContext(MerchantRoutesContext);

  if (intentTrialExpiredUnpaid) {
    return <IntentTrialExpired />;
  }

  if (
    feature !== undefined &&
    isConversionFeatureLocked(feature, lockedFeatures)
  ) {
    return <Navigate replace to={routes.getConversionSubRoute("dashboard")} />;
  }

  if (!checkoutOptimizationEnabled) {
    return <Navigate replace to={routes.getConversionSubRoute("welcome")} />;
  }

  return <Outlet />;
});
